import React from "react";
import { FiChevronRight, FiCreditCard, FiEdit3, FiInfo, FiSave, FiUsers } from "react-icons/fi";
import { maskIban } from "../../../../utils/ibanUtils";
import ParentContributionsSection from "../../finance/ParentContributions";

const ChevronRightIcon = FiChevronRight;
const CreditCardIcon = FiCreditCard;
const EditIcon = FiEdit3;
const InfoIcon = FiInfo;
const SaveIcon = FiSave;
const UsersIcon = FiUsers;

const Field = ({ label, value, onChange, autoComplete }) => (
  <label className="flex min-w-0 flex-col gap-1.5">
    <span className="text-sm font-semibold text-slate-600 dark:text-slate-300">{label}</span>
    <input
      type="text"
      value={value || ""}
      onChange={onChange}
      autoComplete={autoComplete}
      className="min-h-[46px] w-full rounded-2xl border border-slate-200 bg-white/85 px-4 text-base font-medium text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-emerald-400 focus:ring-4 focus:ring-emerald-100 dark:border-slate-700 dark:bg-slate-950/40 dark:text-slate-100 dark:focus:ring-emerald-900/30"
    />
  </label>
);

const EmptyBankRow = ({ t, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="group flex min-h-[72px] w-full items-center gap-4 rounded-[1.6rem] border border-emerald-200 bg-emerald-50/70 px-5 text-left transition hover:-translate-y-0.5 hover:border-emerald-300 hover:bg-emerald-50 hover:shadow-lg hover:shadow-emerald-100/50 active:translate-y-0 dark:border-emerald-800/60 dark:bg-emerald-950/20 dark:hover:bg-emerald-950/35 dark:hover:shadow-emerald-950/20"
  >
    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/80 text-emerald-700 ring-1 ring-emerald-100 dark:bg-slate-950/40 dark:text-emerald-300 dark:ring-emerald-900/50" aria-hidden="true">
      <CreditCardIcon />
    </span>
    <span className="min-w-0 flex-1 text-base font-black text-emerald-800 dark:text-emerald-200">
      {t("finance.add_account", { defaultValue: "Add bank account" })}
    </span>
    <ChevronRightIcon className="text-emerald-800 transition group-hover:translate-x-1 dark:text-emerald-200" aria-hidden="true" />
  </button>
);

const FinanceTab = ({
  t,
  editBankMode,
  setEditBankMode,
  bankForm,
  setBankForm,
  handleSubmitBankChange,
  bankAccount,
  hasPendingBankMutation,
  currentYearFee,
  formatCurrency,
  billableChildren,
  familyId = null,
}) => {
  const hasFee = currentYearFee !== null && currentYearFee !== 0;
  // What each child pays per month right now; a different contribution comes from the backend.
  const feeFor = (child) => Number(child.monthlyContribution ?? currentYearFee);
  const totalFee = hasFee ? billableChildren.reduce((sum, child) => sum + feeFor(child), 0) : 0;

  const startAddBank = () => {
    setBankForm({ proposedIban: "", proposedAccountHolderName: "" });
    setEditBankMode(true);
  };

  const startEditBank = () => {
    setBankForm({
      proposedIban: bankAccount?.iban || "",
      proposedAccountHolderName: bankAccount?.accountHolderName || "",
    });
    setEditBankMode(true);
  };

  return (
    <div className="mx-auto w-full max-w-5xl space-y-5 text-left motion-safe:animate-[financeScreenSlideIn_420ms_cubic-bezier(0.22,1,0.36,1)_both]">
      <header className="flex items-center gap-4 rounded-3xl bg-gradient-to-br from-emerald-50 to-white p-5 ring-1 ring-emerald-100 dark:from-emerald-950/20 dark:to-slate-950/20 dark:ring-emerald-900/50">
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-emerald-600 text-white shadow-sm" aria-hidden="true">
          <CreditCardIcon />
        </span>
        <span>
          <h2 className="text-2xl font-black tracking-tight text-slate-950 dark:text-slate-50">
            {t("parentPortal.finance", { defaultValue: "Finance" })}
          </h2>
          <p className="mt-1 text-sm font-medium text-slate-500 dark:text-slate-400">
            {t("parentPortal.finance_subtitle", { defaultValue: "Bank account and yearly fees" })}
          </p>
        </span>
      </header>

      <section className="rounded-[2rem] border border-slate-200 bg-white/80 p-5 shadow-sm dark:border-slate-800 dark:bg-slate-950/30 sm:p-6">
        <div className="mb-4 flex items-center justify-between gap-3">
          <h3 className="text-lg font-black text-slate-950 dark:text-slate-50">
            {t("finance.registered_account", { defaultValue: "Registered bank account" })}
          </h3>
          {!editBankMode && bankAccount && !hasPendingBankMutation && (
            <button
              type="button"
              onClick={startEditBank}
              className="inline-flex min-h-[40px] items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white/80 px-4 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950/30 dark:text-slate-200"
            >
              <EditIcon aria-hidden="true" />
              {t("common.actions.edit", { defaultValue: "Edit" })}
            </button>
          )}
        </div>

        <div key={editBankMode ? "bank-edit" : "bank-view"} className="motion-safe:animate-[financeContentIn_260ms_cubic-bezier(0.22,1,0.36,1)_both]">
          {editBankMode ? (
            <form className="space-y-4" onSubmit={(event) => event.preventDefault()}>
              <div className="grid gap-4 sm:grid-cols-2">
                <Field
                  label={t("finance.iban", { defaultValue: "IBAN" })}
                  value={bankForm.proposedIban}
                  autoComplete="off"
                  onChange={(event) => setBankForm({ ...bankForm, proposedIban: event.target.value })}
                />
                <Field
                  label={t("finance.account_holder", { defaultValue: "Account holder" })}
                  value={bankForm.proposedAccountHolderName}
                  autoComplete="name"
                  onChange={(event) => setBankForm({ ...bankForm, proposedAccountHolderName: event.target.value })}
                />
              </div>

              <div className="flex flex-col-reverse gap-3 pt-2 sm:flex-row sm:justify-end">
                <button
                  type="button"
                  onClick={() => setEditBankMode(false)}
                  className="inline-flex min-h-[44px] items-center justify-center rounded-2xl border border-slate-200 bg-white/80 px-5 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-950/30 dark:text-slate-200"
                >
                  {t("common.cancel", { defaultValue: "Cancel" })}
                </button>
                <button
                  type="button"
                  onClick={handleSubmitBankChange}
                  disabled={hasPendingBankMutation}
                  title={hasPendingBankMutation ? t("parentPortal.bank_change_pending", { defaultValue: "Your new bank account number has been saved and is ready for administrative processing." }) : undefined}
                  className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-2xl bg-emerald-600 px-5 text-sm font-bold text-white shadow-sm transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <SaveIcon aria-hidden="true" />
                  {t("common.save", { defaultValue: "Save" })}
                </button>
              </div>
            </form>
          ) : bankAccount ? (
            <div className="space-y-3">
              <div className="flex items-start gap-4 rounded-[1.6rem] border border-slate-200 bg-white/75 p-4 shadow-sm dark:border-slate-800 dark:bg-slate-950/30">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100 dark:bg-emerald-950/40 dark:text-emerald-300 dark:ring-emerald-900/50" aria-hidden="true">
                  <CreditCardIcon />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-xs font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                    {t("finance.iban", { defaultValue: "IBAN" })}
                  </span>
                  <span className="mt-1 block break-words text-lg font-black text-slate-950 dark:text-slate-50">
                    {maskIban(bankAccount.iban)}
                  </span>
                  <span className="mt-2 block text-sm font-semibold text-slate-500 dark:text-slate-400">
                    {bankAccount.accountHolderName}
                  </span>
                </span>
                {hasPendingBankMutation && (
                  <span className="rounded-full bg-orange-100 px-3 py-1 text-xs font-black text-orange-800 dark:bg-orange-900/40 dark:text-orange-200">
                    {t("parentPortal.bank_change_status", { defaultValue: "Change received" })}
                  </span>
                )}
              </div>
              {hasPendingBankMutation && (
                <p className="rounded-2xl bg-orange-50 px-4 py-3 text-sm font-semibold text-orange-700 ring-1 ring-orange-100 dark:bg-orange-950/25 dark:text-orange-200 dark:ring-orange-900/40">
                  {t("parentPortal.bank_change_pending", { defaultValue: "Your new bank account number has been saved and is ready for administrative processing." })}
                </p>
              )}
            </div>
          ) : hasPendingBankMutation ? (
            <p className="rounded-2xl bg-orange-50 px-4 py-3 text-sm font-semibold text-orange-700 ring-1 ring-orange-100 dark:bg-orange-950/25 dark:text-orange-200 dark:ring-orange-900/40">
              {t("parentPortal.bank_change_pending", { defaultValue: "Your new bank account number has been saved and is ready for administrative processing." })}
            </p>
          ) : (
            <EmptyBankRow t={t} onClick={startAddBank} />
          )}
        </div>
      </section>

      {hasFee && (
        <section className="rounded-[2rem] border border-emerald-100 bg-emerald-50/55 p-5 shadow-sm dark:border-emerald-900/50 dark:bg-emerald-950/20 sm:p-6">
          <div className="flex items-start gap-3">
            <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-white/80 text-emerald-700 ring-1 ring-emerald-100 dark:bg-slate-950/40 dark:text-emerald-300 dark:ring-emerald-900/50" aria-hidden="true">
              <InfoIcon />
            </span>
            <p className="text-sm font-semibold leading-6 text-emerald-900 dark:text-emerald-100">
              {t("parentPortal.finance_explanation", { fee: formatCurrency(currentYearFee) })}
            </p>
          </div>
        </section>
      )}

      <ParentContributionsSection
        familyId={familyId}
        fallback={(
          <section className="rounded-[2rem] border border-slate-200 bg-white/80 p-5 shadow-sm dark:border-slate-800 dark:bg-slate-950/30 sm:p-6">
            <div className="mb-4 flex items-center gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100 dark:bg-emerald-950/40 dark:text-emerald-300 dark:ring-emerald-900/50" aria-hidden="true">
                <UsersIcon />
              </span>
              <h3 className="text-lg font-black text-slate-950 dark:text-slate-50">
                {t("parentPortal.yearly_fee", { defaultValue: "Yearly fee" })}
              </h3>
            </div>

            {hasFee && billableChildren.length > 0 ? (
              <div className="space-y-3">
                {billableChildren.map((child, index) => (
                  <div
                    key={child.id}
                    className="flex items-center justify-between gap-4 rounded-2xl border border-slate-100 bg-white/70 px-4 py-3 text-sm font-bold text-slate-800 shadow-sm dark:border-slate-800 dark:bg-slate-950/30 dark:text-slate-100 motion-safe:animate-[financeRowIn_280ms_cubic-bezier(0.22,1,0.36,1)_both]"
                    style={{ animationDelay: `${index * 45}ms` }}
                  >
                    <span className="min-w-0 truncate">{child.firstName}</span>
                    <span className="shrink-0 font-receipt tabular-nums text-emerald-700 dark:text-emerald-300">{formatCurrency(feeFor(child))}</span>
                  </div>
                ))}
                <div className="flex items-center justify-between gap-4 rounded-2xl bg-slate-950 px-4 py-4 text-base font-black text-white shadow-sm dark:bg-emerald-950/40 dark:text-emerald-50 dark:shadow-none dark:ring-1 dark:ring-emerald-800/60">
                  <span>{t("parentPortal.total", { defaultValue: "Total" })}</span>
                  <span className="font-receipt tabular-nums">{formatCurrency(totalFee)}</span>
                </div>
              </div>
            ) : (
              <p className="rounded-2xl bg-slate-50 px-4 py-4 text-sm font-semibold italic text-slate-500 ring-1 ring-slate-100 dark:bg-slate-950/30 dark:text-slate-400 dark:ring-slate-800">
                {t("parentPortal.no_fee_data", { defaultValue: "No fee data available." })}
              </p>
            )}
          </section>
        )}
      />

      <style>{`
        @keyframes financeScreenSlideIn {
          from { opacity: 0; transform: translateX(32px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes financeContentIn {
          from { opacity: 0; transform: translateX(12px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes financeRowIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default FinanceTab;
